import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { searchTickets, getTicketViews } from '../api/tickets';
import { supabase } from '../supabaseClient';

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;
const DEFAULT_PRIORITIES = [0, 1, 2, 3, 4];

/**
 * useTicketSearch — 搜尋/篩選/分頁/排序狀態管理
 *
 * @param {Object} opts
 * @param {number|null} opts.projectId - project 模式
 * @param {string|null} opts.userId - my-tasks 模式
 * @param {string|null} opts.viewerUserId - 當前檢視者 ID（用於未讀判斷）
 */
const useTicketSearch = ({ projectId = null, userId = null, companyId = null, viewerUserId = null, defaultIncludeClosed = false, extendedSearch = false, statusOptions = [], projects = [], refreshKey = 0 } = {}) => {
  // ── 搜尋 ──
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef(null);

  // 計算預設狀態
  const defaultStatuses = useMemo(() => {
    if (!statusOptions || statusOptions.length === 0) return [];
    if (defaultIncludeClosed) return statusOptions.map(s => s.value);
    return statusOptions.filter(s => s.value !== 'closed').map(s => s.value);
  }, [defaultIncludeClosed, statusOptions]);

  // ── 篩選 ──
  // 初始值：若 statusOptions 已載入則設為預設，否則空陣列（API fallback: neq closed）
  const [statusFilter, setStatusFilter] = useState(() => {
    if (!statusOptions || statusOptions.length === 0) return [];
    if (defaultIncludeClosed) return statusOptions.map(s => s.value);
    return statusOptions.filter(s => s.value !== 'closed').map(s => s.value);
  });
  const [priorityFilter, setPriorityFilter] = useState([...DEFAULT_PRIORITIES]);
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  const [projectFilter, setProjectFilter] = useState(() =>
    projects.length > 0 ? projects.map(p => p.id) : []
  );

  // refs（供 reset effect / clearAll 使用，避免加入 deps 觸發不必要的 effect）
  const defaultStatusesRef = useRef(defaultStatuses);
  defaultStatusesRef.current = defaultStatuses;
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  // statusOptions 非同步載入後設定預設狀態（僅首次）
  const statusInitRef = useRef((!statusOptions ? 0 : statusOptions.length) > 0);
  useEffect(() => {
    if (statusInitRef.current) return;
    if (defaultStatuses.length > 0) {
      statusInitRef.current = true;
      setStatusFilter(defaultStatuses);
    }
  }, [defaultStatuses]);

  // projects 非同步載入後設定預設專案篩選（僅首次，Inclusive：預設全勾）
  const projectInitRef = useRef(projects.length > 0);
  useEffect(() => {
    if (projectInitRef.current) return;
    if (projects.length > 0) {
      projectInitRef.current = true;
      setProjectFilter(projects.map(p => p.id));
    }
  }, [projects]);
  // ── 分頁 ──
  const [page, setPage] = useState(1);

  // ── 排序 ──
  const [sortBy, setSortBy] = useState('ticket_no');
  const [sortOrder, setSortOrder] = useState('desc');

  // ── 資料 ──
  const [tickets, setTickets] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);

  // debounce 搜尋
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, DEBOUNCE_MS);
  }, []);

  // 篩選變更時 reset page
  useEffect(() => {
    setPage(1);
  }, [statusFilter, priorityFilter, assigneeFilter, projectFilter]);

  // projectId / userId 變更時清空篩選（首次 mount 跳過：初始值已正確，
  // 避免建立新 array ref 導致 loadData 重建、searchTickets 重複呼叫）
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setSearch('');
    setDebouncedSearch('');
    setStatusFilter(defaultStatusesRef.current.length > 0 ? [...defaultStatusesRef.current] : []);
    setPriorityFilter([...DEFAULT_PRIORITIES]);
    setAssigneeFilter(prev => prev.length === 0 ? prev : []);
    setProjectFilter(projectsRef.current.length > 0 ? projectsRef.current.map(p => p.id) : []);
    setPage(1);
    setSortBy('ticket_no');
    setSortOrder('desc');
    initialLoadDone.current = false;
  }, [projectId, userId, companyId]);

  // ── 載入資料 ──
  const loadData = useCallback(async () => {
    // companyId 尚未準備好（undefined 或 null），等待 AuthContext 初始化
    if (!companyId) return;
    // statusFilter 尚未從 statusOptions 初始化，等 statusOptions 載入後再打 API
    if (statusFilter.length === 0 && !statusInitRef.current) return;
    // 首次載入才顯示 skeleton，後續 refresh 靜默更新
    if (!initialLoadDone.current) setLoading(true);
    // Inclusive 模式：全選 → 不篩選，部分選 → 傳選中 IDs，全不選 → 空結果
    if (!projectId && projectsRef.current.length > 0 && projectFilter.length === 0) {
      setTickets([]);
      setTotalCount(0);
      setLoading(false);
      initialLoadDone.current = true;
      return;
    }
    const effectiveProjectIds = (projectsRef.current.length > 0 && projectFilter.length === projectsRef.current.length)
      ? [] : projectFilter;
    const { data, count, error } = await searchTickets({
      projectId,
      projectIds: effectiveProjectIds,
      userId,
      companyId,
      search: debouncedSearch,
      status: statusFilter,
      priorities: priorityFilter,
      assigneeIds: assigneeFilter,
      extendedSearch,
      page,
      pageSize: PAGE_SIZE,
      sortBy,
      sortOrder,
    });

    if (!error) {
      // 取得未讀狀態
      if (viewerUserId && data.length > 0) {
        const ticketIds = data.map(t => t.id);
        const { data: views } = await getTicketViews(ticketIds, viewerUserId);
        const viewMap = new Map(views.map(v => [v.ticket_id, v.last_viewed_at]));
        const enriched = data.map(t => ({
          ...t,
          has_new_activity: t.last_activity_at && (
            !viewMap.has(t.id) ||
            new Date(t.last_activity_at) > new Date(viewMap.get(t.id))
          ),
        }));
        setTickets(enriched);
      } else {
        setTickets(data);
      }
      setTotalCount(count);
    }
    setLoading(false);
    initialLoadDone.current = true;
  }, [
    projectId,
    userId,
    companyId,
    viewerUserId,
    debouncedSearch,
    statusFilter,
    priorityFilter,
    assigneeFilter,
    projectFilter,
    page,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  // ── Realtime：監聯 tickets 表的 last_activity_at 更新 ──
  // 用 ref 持有最新的 loadData，避免 loadData 變動導致頻繁重新訂閱
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  useEffect(() => {
    if (!companyId) return;

    const channel = supabase
      .channel(`tickets-activity-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'tickets',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          loadDataRef.current();
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [companyId]);

  // ── 排序切換 ──
  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortBy(field);
        setSortOrder('desc');
      }
      setPage(1);
    },
    [sortBy]
  );

  // ── 篩選管理 ──
  const activeFilterCount = useMemo(() => {
    let count = 0;
    // 狀態：跟預設不同才算有篩選
    if (statusFilter.length > 0) {
      const isDefault = defaultStatuses.length > 0 &&
        defaultStatuses.length === statusFilter.length &&
        defaultStatuses.every(v => statusFilter.includes(v));
      if (!isDefault) count++;
    }
    // 優先度：跟預設不同才算有篩選
    const isPriorityDefault = priorityFilter.length === DEFAULT_PRIORITIES.length &&
      DEFAULT_PRIORITIES.every(v => priorityFilter.includes(v));
    if (!isPriorityDefault) count++;
    if (assigneeFilter.length > 0) count++;
    // Inclusive：不是全選才算有篩選
    if (projects.length > 0 && projectFilter.length < projects.length) count++;
    return count;
  }, [statusFilter, defaultStatuses, priorityFilter, assigneeFilter, projectFilter, projects]);

  const clearAllFilters = useCallback(() => {
    setStatusFilter(defaultStatusesRef.current.length > 0 ? [...defaultStatusesRef.current] : []);
    setPriorityFilter([...DEFAULT_PRIORITIES]);
    setAssigneeFilter([]);
    setProjectFilter(projectsRef.current.length > 0 ? projectsRef.current.map(p => p.id) : []);
    setSearch('');
    setDebouncedSearch('');
    setPage(1);
  }, []);

  // ── 分頁計算 ──
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const from = totalCount > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, totalCount);

  return {
    // 資料
    tickets,
    totalCount,
    loading,
    // 搜尋
    search,
    handleSearchChange,
    // 篩選
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    assigneeFilter,
    setAssigneeFilter,
    projectFilter,
    setProjectFilter,
    // 分頁
    page,
    setPage,
    totalPages,
    from,
    to,
    pageSize: PAGE_SIZE,
    // 排序
    sortBy,
    sortOrder,
    handleSort,
    // 管理
    activeFilterCount,
    clearAllFilters,
    refresh: loadData,
  };
};

export default useTicketSearch;
